import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";

import styled from "styled-components/macro";

import { useRecurringGifts } from "CrossFeatureComponents/UserRecurringGifts/RecurringGiftsContext";
import classNames from "classnames";

import FnrHoverButton from "UI/FnrHoverButton";
import FnrIcon from "UI/FnrIcon";

import { formatAmountForDisplay } from "Utilities/Amount/formatAmountForDisplay";

import {
  StatBoxWrapper,
  StatNumber,
  StatText,
  statsBoxVariants,
} from "./StatBox";

export default function NextPayments({ payments }) {
  return (
    <Wrapper>
      {payments?.map((payment, index) => {
        const amount = formatAmountForDisplay(payment.amount, {
          roundToWholeDollar: true,
          isCents: true,
        });

        let date;
        const nextPaymentDateCachedForDisplayOnly =
          payment.nextPaymentDateCachedForDisplayOnly;
        if (!nextPaymentDateCachedForDisplayOnly) {
          date = "N/A";
        } else {
          date = new Date(
            `${nextPaymentDateCachedForDisplayOnly}T00:00:00`
          ).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
          });
        }

        let nextPaymentVerbiage = index === 0 ? "Next Payment" : false;
        if (index === 0 && payments.length > 1) {
          nextPaymentVerbiage += "s • ";
          nextPaymentVerbiage += date;
        }

        return (
          <StatBox
            key={payment.ID}
            text={nextPaymentVerbiage}
            number={amount}
            fundName={payment.fundName}
            date={payments.length === 1 ? date : false}
            paymentMethod={payment.paymentMethod}
            isLastInList={index === payments.length - 1}
            userCanEdit={payment.userCanEdit}
            recurringGiftID={payment.ID}
          />
        );
      })}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 60px;
  flex-wrap: wrap;
  @media (max-width: 540px) {
    flex-direction: column;
    gap: 30px;
    align-items: flex-start;
  }
`;

const NoWrap = styled.span`
  white-space: nowrap;
`;

function StatBox({
  text,
  number,
  fundName,
  date,
  paymentMethod,
  isLastInList,
  userCanEdit,
  recurringGiftID,
}) {
  const history = useHistory();
  const location = useLocation();
  const { state } = useRecurringGifts();

  const recurringGiftsAreLoaded = state.recurringGifts?.length > 0;

  const handleEditClick = () => {
    history.push({
      pathname: `${location.pathname}/recurring/${recurringGiftID}`,
      search: location.search,
    });
  };

  return (
    <NextPaymentWrapper
      variants={statsBoxVariants}
      className={classNames({
        has_header_text: Boolean(text),
        last_child: isLastInList,
      })}
    >
      {text && (
        <HeaderWrapper>
          <NextPaymentHeader>{text}</NextPaymentHeader>
        </HeaderWrapper>
      )}
      <StatNumberFlex>
        ${number}
        {date && (
          <>
            <Dot>{" • "}</Dot>
            <NoWrap>{date}</NoWrap>
          </>
        )}
        {userCanEdit && recurringGiftsAreLoaded && (
          <EditButtonWrapper>
            <FnrHoverButton onClick={handleEditClick}>
              <FnrIcon fad fa-pencil />
            </FnrHoverButton>
          </EditButtonWrapper>
        )}
      </StatNumberFlex>
      <StatSubtext>
        {fundName} {" • "}{" "}
        {paymentMethod.nickname ? (
          <PaymentInfo>{paymentMethod.nickname}</PaymentInfo>
        ) : (
          <PaymentInfo>
            {paymentMethod.paymentType === "credit_card" ? (
              <>{paymentMethod.cardType}</>
            ) : (
              <>Bank</>
            )}{" "}
            {paymentMethod.last4}
          </PaymentInfo>
        )}
      </StatSubtext>
    </NextPaymentWrapper>
  );
}

const StatSubtext = styled.div`
  font-weight: 300;
`;
const StatNumberFlex = styled(StatNumber)`
  display: flex;
  align-items: center;
`;
const NextPaymentHeader = styled(StatText)`
  white-space: nowrap;
`;
const HeaderWrapper = styled.div`
  display: flex;
`;
const NextPaymentWrapper = styled(StatBoxWrapper)`
  position: relative;
`;

const PaymentInfo = styled.span`
  font-size: 14px;
`;

const EditButtonWrapper = styled.div`
  display: inline-block;
  margin-left: 2px;
  font-size: 14px;
  opacity: 0;
  transform: scale(0.8);
  transition: opacity 0.2s, transform 0.2s;
  ${NextPaymentWrapper}:hover & {
    opacity: 1;
    transform: none;
  }
`;

const Dot = styled.div`
  margin: 0 8px;
`;
